import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { SectionHeader } from "@/components/common/Widgets";
import EligibilityPreview from "@/components/common/EligibilityPreview";
import { Plus, Loader2, ArrowRight, Phone, Inbox, UserPlus, Sparkles, Wallet } from "lucide-react";
import { fmtDate } from "@/lib/format";

const STATUS_COLORS = {
  NEW: "border-info/40 text-info",
  CONTACTED: "border-accent/40 text-accent",
  QUALIFIED: "border-success/40 text-success",
  DISQUALIFIED: "border-muted-foreground/40 text-muted-foreground",
  CONVERTED: "border-accent/60 text-accent bg-accent/5",
  DROPPED: "border-destructive/40 text-destructive",
};

const FALLBACK_LOAN_TYPES = [
  { code: "PERSONAL_LOAN", label: "Personal Loan", category: "RETAIL" },
  { code: "HOUSING_LOAN", label: "Housing Loan", category: "RETAIL" },
  { code: "GOLD_LOAN", label: "Gold Loan", category: "RETAIL" },
  { code: "TW_LOAN", label: "Two-Wheeler Loan", category: "RETAIL" },
  { code: "FW_LOAN", label: "Four-Wheeler Loan", category: "RETAIL" },
  { code: "CC", label: "Cash Credit (CC)", category: "BUSINESS" },
  { code: "TL", label: "Term Loan (TL)", category: "BUSINESS" },
  { code: "SME_CC", label: "SME Cash Credit", category: "BUSINESS" },
];

const RETAIL_RETAIL_TYPES = new Set(["PERSONAL_LOAN", "HOUSING_LOAN"]); // those needing FOIR
const HOUSING_TYPES = new Set(["HOUSING_LOAN", "MORTGAGE"]);
const BUSINESS_TYPES = new Set(["CC", "TL", "SME_CC", "WC_NAYAK", "PROJECT", "MUDRA_KISHORE", "MUDRA_TARUN", "MUDRA_SHISHU", "CGTMSE_HYBRID", "STAND_UP_INDIA"]);

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ sources: [], statuses: [], loan_types: FALLBACK_LOAN_TYPES });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const [l, m] = await Promise.all([api.get("/leads", { params }), api.get("/leads/meta")]);
      setLeads(l.data);
      setMeta({ ...m.data, loan_types: m.data.loan_types || FALLBACK_LOAN_TYPES });
    } catch (e) { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const loanTypeLabel = (code) => meta.loan_types.find((t) => t.code === code)?.label || code || "—";

  return (
    <div className="space-y-5" data-testid="leads-page">
      <SectionHeader
        title="Lead Capture"
        subtitle="Pre-application lead pipeline — capture quickly from any channel and convert qualified leads into full applications."
        action={(
          <div className="flex gap-2 flex-wrap">
            <Select value={filterStatus || "ALL"} onValueChange={(v) => setFilterStatus(v === "ALL" ? "" : v)}>
              <SelectTrigger className="w-44" data-testid="leads-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {meta.statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => setCreating(true)} data-testid="lead-new-button"><Plus className="h-4 w-4 mr-1" />New Lead</Button>
          </div>
        )}
      />
      {loading ? (
        <Card className="p-4 space-y-2">{[0,1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>
      ) : leads.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Inbox className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
          <h3 className="text-base font-semibold heading">No leads yet</h3>
          <p className="text-sm text-muted-foreground mt-1">Capture your first lead from any channel — branch walk-in, web form, DSA, call-center, etc.</p>
          <Button className="mt-4" onClick={() => setCreating(true)} data-testid="leads-empty-new"><UserPlus className="h-4 w-4 mr-1" />Capture New Lead</Button>
        </Card>
      ) : (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr>
              <th className="text-left px-3 py-2.5">When</th>
              <th className="text-left px-3 py-2.5">Name</th>
              <th className="text-left px-3 py-2.5">Mobile</th>
              <th className="text-left px-3 py-2.5">Interest</th>
              <th className="text-left px-3 py-2.5">Amount</th>
              <th className="text-left px-3 py-2.5">Source</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr></thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.id} className="border-t border-border hover:bg-muted/30" data-testid={`lead-row-${l.id}`}>
                  <td className="px-3 py-2 text-xs mono text-muted-foreground">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-2"><div className="font-medium">{l.name}</div></td>
                  <td className="px-3 py-2 mono text-xs"><Phone className="h-3 w-3 inline mr-1 text-muted-foreground" />{l.mobile}</td>
                  <td className="px-3 py-2 text-xs">{l.product_interest ? <Badge variant="outline" className="text-[10px]">{loanTypeLabel(l.product_interest)}</Badge> : "—"}</td>
                  <td className="px-3 py-2 text-xs tabular">{l.loan_amount_lakhs ? `₹${l.loan_amount_lakhs}L` : "—"}</td>
                  <td className="px-3 py-2 text-xs"><Badge variant="outline" className="text-[10px]">{l.source}</Badge></td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[l.status] || ""}`}>{l.status}</Badge></td>
                  <td className="px-3 py-2 text-right">
                    {l.status !== "CONVERTED" ? (
                      <Button size="sm" variant="outline" onClick={() => setConverting(l)} data-testid={`lead-convert-${l.id}`}>Convert<ArrowRight className="h-3 w-3 ml-1" /></Button>
                    ) : (
                      <Link to={`/applications/${l.converted_application_id}`} className="text-xs underline text-accent">View App</Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {creating && <LeadCreateDialog meta={meta} onClose={() => setCreating(false)} onSave={() => { setCreating(false); load(); }} />}
      {converting && <LeadConvertDialog lead={converting} meta={meta} onClose={() => setConverting(null)} onDone={(appId) => { setConverting(null); load(); navigate(`/applications/${appId}`); }} />}
    </div>
  );
}

function LeadCreateDialog({ meta, onClose, onSave }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", mobile: "", email: "", product_interest: "", loan_amount_lakhs: "", purpose: "", source: "BRANCH", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, loan_amount_lakhs: form.loan_amount_lakhs ? Number(form.loan_amount_lakhs) : null };
      await api.post("/leads", payload);
      toast.success("Lead captured");
      onSave();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Lead</DialogTitle><DialogDescription>Lightweight capture — just enough to follow up.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>First name *</Label><Input value={form.first_name} onChange={e => set("first_name", e.target.value)} data-testid="lead-first-name" /></div>
          <div className="space-y-1.5"><Label>Last name</Label><Input value={form.last_name} onChange={e => set("last_name", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Mobile *</Label><Input value={form.mobile} onChange={e => set("mobile", e.target.value)} placeholder="10 digits" data-testid="lead-mobile" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Source *</Label><Select value={form.source} onValueChange={v => set("source", v)}><SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger><SelectContent>{meta.sources?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5">
            <Label>Product Interest</Label>
            <Select value={form.product_interest || "_none_"} onValueChange={(v) => set("product_interest", v === "_none_" ? "" : v)}>
              <SelectTrigger data-testid="lead-product-interest"><SelectValue placeholder="Select a loan product" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">— Not sure yet —</SelectItem>
                {meta.loan_types?.filter((t) => t.category === "RETAIL").length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Retail</div>
                    {meta.loan_types.filter((t) => t.category === "RETAIL").map((t) => (
                      <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                    ))}
                  </>
                )}
                {meta.loan_types?.filter((t) => t.category === "BUSINESS").length > 0 && (
                  <>
                    <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Business</div>
                    {meta.loan_types.filter((t) => t.category === "BUSINESS").map((t) => (
                      <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Amount (₹ L)</Label><Input type="number" value={form.loan_amount_lakhs} onChange={e => set("loan_amount_lakhs", e.target.value)} data-testid="lead-amount" /></div>
          <div className="space-y-1.5"><Label>Purpose</Label><Input value={form.purpose} onChange={e => set("purpose", e.target.value)} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.first_name || !form.mobile} data-testid="lead-save">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save Lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadConvertDialog({ lead, meta, onClose, onDone }) {
  // Pre-fill loan_type from product_interest; fall back to PERSONAL_LOAN
  const initialLoanType = useMemo(() => {
    if (lead.product_interest && meta.loan_types.find((t) => t.code === lead.product_interest)) {
      return lead.product_interest;
    }
    return "PERSONAL_LOAN";
  }, [lead.product_interest, meta.loan_types]);

  const [form, setForm] = useState({
    loan_type: initialLoanType,
    amount_lakhs: lead.loan_amount_lakhs || "",
    tenor_months: 36,
    interest_rate: "12",
    branch: lead.branch || "",
    purpose: lead.purpose || "",
    // Retail (FOIR)
    net_monthly_income: "",
    monthly_expense: "",
    existing_emi: "",
    employer_name: "",
    employer_type: "",
    // Housing
    property_cost_lakhs: "",
    property_location: "",
    ltv_pct: "",
    property_type: "",
    rera_number: "",
    builder_name: "",
    // Business
    cgtmse_requested: false,
  });
  const [busy, setBusy] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lt = form.loan_type;
  const isRetail = RETAIL_RETAIL_TYPES.has(lt);
  const isHousing = HOUSING_TYPES.has(lt);
  const isBusiness = BUSINESS_TYPES.has(lt);

  // Reset eligibility when loan type switches away from retail
  useEffect(() => {
    if (!isRetail) setEligibility(null);
  }, [isRetail]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        loan_type: form.loan_type,
        amount_lakhs: form.amount_lakhs ? Number(form.amount_lakhs) : null,
        tenor_months: form.tenor_months ? Number(form.tenor_months) : null,
        interest_rate: form.interest_rate ? Number(form.interest_rate) : null,
        branch: form.branch || null,
        purpose: form.purpose || null,
      };
      if (isRetail) {
        payload.net_monthly_income = form.net_monthly_income ? Number(form.net_monthly_income) : null;
        payload.monthly_expense = form.monthly_expense ? Number(form.monthly_expense) : null;
        payload.existing_emi = form.existing_emi ? Number(form.existing_emi) : null;
        payload.employer_name = form.employer_name || null;
        payload.employer_type = form.employer_type || null;
        if (eligibility?.current_foir_pct != null) {
          payload.foir_pct = eligibility.current_foir_pct;
        }
      }
      if (isHousing) {
        payload.property_cost_lakhs = form.property_cost_lakhs ? Number(form.property_cost_lakhs) : null;
        payload.property_location = form.property_location || null;
        payload.ltv_pct = form.ltv_pct ? Number(form.ltv_pct) : null;
        payload.property_type = form.property_type || null;
        payload.rera_number = form.rera_number || null;
        payload.builder_name = form.builder_name || null;
      }
      if (isBusiness) {
        payload.cgtmse_requested = !!form.cgtmse_requested;
      }
      const r = await api.post(`/leads/${lead.id}/convert`, payload);
      toast.success("Lead converted to application");
      onDone(r.data.application_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally {
      setBusy(false);
    }
  };

  const verdict = eligibility?.verdict;

  // Block submit when retail and verdict is REJECTED (HO can still override by adjusting amount)
  const submitBlocked = !form.loan_type || busy || (isRetail && verdict === "REJECTED");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Convert Lead → Application</DialogTitle>
          <DialogDescription>Creates a full application (re-uses any existing customer with the same mobile in this tenant). For retail loans, income &amp; expenditure determine eligibility.</DialogDescription>
        </DialogHeader>

        <div className="text-sm bg-muted/30 rounded-lg p-3 flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-muted-foreground" />
          <div className="font-semibold">{lead.name}</div>
          <span className="text-muted-foreground">·</span>
          <span className="mono">{lead.mobile}</span>
          <span className="text-muted-foreground">·</span>
          <Badge variant="outline" className="text-[10px]">{lead.source}</Badge>
          {lead.product_interest && <Badge variant="outline" className="text-[10px] border-accent/40 text-accent">Interest: {meta.loan_types.find((t) => t.code === lead.product_interest)?.label || lead.product_interest}</Badge>}
        </div>

        {/* --- Loan Type --- */}
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Loan Type *</Label>
          <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
            <SelectTrigger data-testid="convert-loan-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {meta.loan_types?.filter((t) => t.category === "RETAIL").length > 0 && (
                <>
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Retail</div>
                  {meta.loan_types.filter((t) => t.category === "RETAIL").map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                  ))}
                </>
              )}
              {meta.loan_types?.filter((t) => t.category === "BUSINESS").length > 0 && (
                <>
                  <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Business</div>
                  {meta.loan_types.filter((t) => t.category === "BUSINESS").map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* --- Common amount / tenor / ROI --- */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label>Amount (₹ Lakhs)</Label><Input type="number" step="0.1" value={form.amount_lakhs} onChange={(e) => set("amount_lakhs", e.target.value)} data-testid="convert-amount" /></div>
          <div className="space-y-1.5"><Label>Tenor (months)</Label><Input type="number" value={form.tenor_months} onChange={(e) => set("tenor_months", e.target.value)} data-testid="convert-tenor" /></div>
          <div className="space-y-1.5"><Label>ROI % p.a.</Label><Input type="number" step="0.1" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} data-testid="convert-roi" /></div>
          <div className="space-y-1.5"><Label>Branch</Label><Input value={form.branch} onChange={(e) => set("branch", e.target.value)} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => set("purpose", e.target.value)} /></div>
        </div>

        {/* --- Retail (PERSONAL / HOUSING) — Income & Expenditure --- */}
        {isRetail && (
          <div className="space-y-3" data-testid="convert-retail-section">
            <Separator />
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-accent" />
              <h4 className="text-sm font-semibold heading">Income & Expenditure (drives loan eligibility)</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Net Monthly Income (₹) *</Label><Input type="number" value={form.net_monthly_income} onChange={(e) => set("net_monthly_income", e.target.value)} placeholder="e.g., 80000" data-testid="convert-income" /></div>
              <div className="space-y-1.5"><Label>Monthly Expense (₹)</Label><Input type="number" value={form.monthly_expense} onChange={(e) => set("monthly_expense", e.target.value)} placeholder="household burn (excl. EMIs)" data-testid="convert-expense" /></div>
              <div className="space-y-1.5"><Label>Existing EMI (₹)</Label><Input type="number" value={form.existing_emi} onChange={(e) => set("existing_emi", e.target.value)} placeholder="sum of all running EMIs" data-testid="convert-existing-emi" /></div>
              <div className="space-y-1.5"><Label>Employer</Label><Input value={form.employer_name} onChange={(e) => set("employer_name", e.target.value)} /></div>
              <div className="space-y-1.5">
                <Label>Employer Type</Label>
                <Select value={form.employer_type || "_none_"} onValueChange={(v) => set("employer_type", v === "_none_" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">—</SelectItem>
                    {["Government", "PSU", "MNC", "Private", "Self-Employed"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Eligibility Preview Card */}
            <EligibilityPreview
              inputs={{
                monthly_income: form.net_monthly_income,
                monthly_expense: form.monthly_expense,
                existing_emi: form.existing_emi,
                tenor_months: form.tenor_months,
                interest_rate_pa: form.interest_rate,
                proposed_amount_lakhs: form.amount_lakhs,
                max_foir_pct: 50,
              }}
              onResult={setEligibility}
              dataTestid="convert-eligibility-preview"
            />
          </div>
        )}

        {/* --- Housing-specific --- */}
        {isHousing && (
          <div className="space-y-3" data-testid="convert-housing-section">
            <Separator />
            <h4 className="text-sm font-semibold heading">Property Details</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Property Cost (₹ Lakhs)</Label><Input type="number" step="0.1" value={form.property_cost_lakhs} onChange={(e) => set("property_cost_lakhs", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>LTV %</Label><Input type="number" step="0.1" value={form.ltv_pct} onChange={(e) => set("ltv_pct", e.target.value)} placeholder="≤ 90% small / ≤ 80% above ₹30L" /></div>
              <div className="space-y-1.5">
                <Label>Property Type</Label>
                <Select value={form.property_type || "_none_"} onValueChange={(v) => set("property_type", v === "_none_" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">—</SelectItem>
                    {["New", "Resale", "Construction"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2"><Label>Property Location</Label><Input value={form.property_location} onChange={(e) => set("property_location", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>RERA No.</Label><Input value={form.rera_number} onChange={(e) => set("rera_number", e.target.value)} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Builder Name</Label><Input value={form.builder_name} onChange={(e) => set("builder_name", e.target.value)} /></div>
            </div>
          </div>
        )}

        {/* --- Business-specific --- */}
        {isBusiness && (
          <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3 flex items-center justify-between" data-testid="convert-business-section">
            <div>
              <div className="text-sm font-medium">CGTMSE Coverage Requested</div>
              <div className="text-xs text-muted-foreground">Hybrid model available; concessional fee for NE region</div>
            </div>
            <input type="checkbox" className="h-4 w-4 accent-accent" checked={!!form.cgtmse_requested} onChange={(e) => set("cgtmse_requested", e.target.checked)} data-testid="convert-cgtmse" />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitBlocked} data-testid="convert-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            Convert to Application
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
